"use client";

import { useState } from "react";
import { ChevronDown } from "lucide-react";
import {
  FormControl,
  MenuItem,
  Select,
  Stack,
  Typography,
  Box,
} from "@mui/material";
import {
  FEATURED_AI_MODEL_IDS,
  FEATURED_AI_MODEL_LABELS,
  FEATURED_AI_MODEL_DESCRIPTIONS,
  isFreeModelId,
} from "@/ai/model/model-catalog";
import type { OpenRouterModel } from "@/ai/model/openrouter-models";
import { modelLabel } from "@/lib/ai-model-ui";
import { ModelPickerModal } from "./model-picker-modal";

export function ModelPicker({
  value,
  onChange,
  models,
  compact = false,
  isAdmin = false,
}: {
  value: string;
  onChange: (value: string) => void;
  models: OpenRouterModel[];
  compact?: boolean;
  isAdmin?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const featuredIds = FEATURED_AI_MODEL_IDS.filter(
    (id) => isAdmin || !isFreeModelId(id),
  );
  return (
    <>
      <FormControl size="small" fullWidth>
        {!compact ? (
          <Typography
            variant="caption"
            sx={{ mb: 0.75, color: "hsl(var(--muted-foreground))" }}
          >
            Modelo da próxima mensagem
          </Typography>
        ) : null}
        <Select
          value={
            featuredIds.includes(
              value as (typeof FEATURED_AI_MODEL_IDS)[number],
            )
              ? value
              : "__other__"
          }
          onChange={(event) => {
            if (event.target.value === "__other__") setOpen(true);
            else onChange(event.target.value);
          }}
          renderValue={() => modelLabel(value, models)}
          IconComponent={ChevronDown}
          aria-label="Modelo da próxima mensagem"
          sx={{ borderRadius: 1.5, bgcolor: "hsl(var(--card))" }}
        >
          {featuredIds.map((id) => (
            <MenuItem key={id} value={id}>
              <Stack sx={{ maxWidth: 280 }}>
                <Typography variant="body2" fontWeight={750}>
                  {FEATURED_AI_MODEL_LABELS[id]}
                </Typography>
                {!compact ? (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ whiteSpace: "normal" }}
                  >
                    {FEATURED_AI_MODEL_DESCRIPTIONS[id] ??
                      "Modelo disponível no OpenRouter."}
                  </Typography>
                ) : null}
              </Stack>
            </MenuItem>
          ))}
          <MenuItem value="__other__">
            <Box>
              <Typography variant="body2" fontWeight={750}>
                Outro modelo…
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Escolher no catálogo completo
              </Typography>
            </Box>
          </MenuItem>
        </Select>
      </FormControl>
      <ModelPickerModal
        open={open}
        onClose={() => setOpen(false)}
        onSelect={onChange}
        models={models}
        value={value}
      />
    </>
  );
}
